import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Fallback for {@link useCloseOrFallback}: a literal route, or a resolver run
 * only when there is no in-app history to pop. The resolver may answer
 * synchronously or asynchronously (T013: the desktop event modal resolves the
 * EVENT's canonical territory through the registry before answering).
 */
export type CloseFallback = string | (() => string | Promise<string>);

/**
 * Returns a "close / go back" handler that never traps the user.
 *
 * `navigate(-1)` is only safe when the current entry was pushed by an earlier
 * in-app navigation. On a direct deeplink (pasted URL, shared link, email link,
 * new tab) the app owns a single history entry, so `navigate(-1)` either does
 * nothing — leaving a modal permanently open — or kicks the user off the site.
 *
 * React Router stores its position in the history stack as `idx` on
 * `window.history.state`. `idx > 0` means there is an in-app entry to pop;
 * anything else (0, null on the very first entry, or a missing state object)
 * means this is the entry the session started on, so we replace it with the
 * fallback route instead of popping.
 *
 * Note `window.history.length` is NOT a valid substitute: it counts entries
 * from other origins visited in the same tab, so it is frequently > 1 on a
 * deeplink where the app still has nowhere to go back to.
 *
 * In v2 callers pass the discovery-built fallback (or a resolver); a bare
 * `'/'`/`'/esplora'` is also fine — the v2 entry gate central-normalizes a
 * generic fallback exactly like any other generic entry.
 *
 * @param fallback route (or resolver) used when there is no in-app history
 */
export function useCloseOrFallback(fallback: CloseFallback): () => void {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = (window.history.state as { idx?: number | null } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }
    const target = typeof fallback === 'function' ? fallback() : fallback;
    if (typeof target === 'string') {
      navigate(target, { replace: true });
      return;
    }
    // A rejecting resolver must not dead-end the close: fail to the safe
    // generic home, which the v2 entry gate central-normalizes like any other
    // bare entry (review-T013 S-1).
    void target
      .then((to) => navigate(to, { replace: true }))
      .catch(() => navigate('/', { replace: true }));
  }, [navigate, fallback]);
}
